import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { toQuery } from "../../../helpers/general";
import { general } from "../../../locales/general";
import BoxWrapper from "../../../components/ui/BoxWrapper";
import Pagination from "../../../components/ui/Pagination";
import RenderTable from "../../../components/ui/RenderTable";
import AdminRoute from "../../auth/RestrictedRoute";
import apiService from "../../../service/apiService";
import { useCookie } from "../../../hooks/useCookie";
import { useDebounce } from "../../../hooks/useDebounce";
import TaxiSpecialZonesTable from "./TaxiSpecialZonesTable";
import { useToast } from "../../../hooks/useToast";
import { SpecialZoneSvg } from "../../../components/icons/SidebarIcons";

export default function TaxiSpecialZones() {
  const { token } = useCookie("vToken");
  const [currentPage, setCurrentPage] = useState(1);
  const [query, setQuery] = useState<string>("");
  const { toastError, toastSuccess } = useToast();
  const searchQuery = useDebounce(query, 100);
  const queryClient = useQueryClient();

  const endpoint = `rides/special-zones${toQuery({
    page: currentPage,
    query: searchQuery,
  })}`;

  const { data, isLoading } = useQuery<PaginatedResponse<RideSpecialZone>>({
    queryKey: ["getTaxiSpecialZones", endpoint],
    queryFn: () => apiService.MakeGetRequest(endpoint, token),
  });

  const deleteSpecialZoneMutation = useMutation({
    mutationFn: (id: string) =>
      apiService.MakeDeleteRequest(`rides/special-zones/${id}`, token),
  });

  const deleteSpecialZone = (id: string) => {
    deleteSpecialZoneMutation.mutate(id, {
      onSuccess: () => {
        toastSuccess("Zone supprimée avec succès");
        queryClient.invalidateQueries(["getTaxiSpecialZones", endpoint]);
      },
      onError: () => {
        toastError(general.fr.message.operationFailed);
      },
    });
  };

  const taxiSpecialZones = data?.items ?? [];

  return (
    <AdminRoute>
      <section>
        <h5 className="fs-4 fw-bold mb-2">Zones spéciaux</h5>
        <BoxWrapper>
          <div className="d-flex align-items-center justify-content-end">
            <div
              className="d-flex align-items-center justify-content-center gap-3 bg-gradient-primary p-3"
              style={{ borderTopRightRadius: "8px", alignSelf: "flex-start" }}
            >
              <SpecialZoneSvg width="30" height="30" className="fill-white" />
              <p className="fs-5 fw-bold m-0  text-white text-pre">
                {data?.meta?.totalItems}{" "}
                {data?.meta?.totalItems === 1
                  ? "Zone spéciale"
                  : "Zones spéciales"}
              </p>
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-between px-3 mt-3">
            <Link
              to="/taxi/special-zones/new"
              className="btn btn-primary text-white w-25"
            >
              Ajouter une nouvelle zone
            </Link>
            <div className="d-flex gap-2">
              <div className="d-flex gap-2 w-100">
                {/* <select
                  className="form-select !w-16 border  outline-none rounded-full p-2 !text-gray fs-6"
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value)}
                >
                  <option value="createdAt">Date de création</option>
                  <option value="name">Nom</option>
                  <option value="discount">Réduction</option>
                </select> */}

                {/* <select
                  className="form-select !w-16 border  outline-none rounded-full p-2 text-gray fs-6"
                  value={sortOrder}
                  onChange={(e) =>
                    setSortOrder(e.target.value as "ASC" | "DESC")
                  }
                >
                  <option value="ASC">Ascendant</option>
                  <option value="DESC">Descendant</option>
                </select> */}
              </div>

              <div className="search-form">
                <div className=" input-field">
                  <MagnifyingGlassIcon color="gray" width={20} />
                  <input
                    type="text"
                    className="w-50 border-0  outline-none rounded-sm p-1 text-gray fs-6"
                    placeholder="Rechercher"
                    onChange={(e) => setQuery(e?.target?.value?.toLowerCase())}
                    defaultValue={query}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="table-responsive">
            <RenderTable
              loading={isLoading}
              render={() =>
                taxiSpecialZones?.length > 0 ? (
                  <TaxiSpecialZonesTable
                    specialZones={taxiSpecialZones}
                    deleteSpecialZone={deleteSpecialZone}
                    isDeleting={deleteSpecialZoneMutation.isLoading}
                  />
                ) : (
                  <p className="text-center fs-5 my-2">{general.fr.noData}</p>
                )
              }
            />
          </div>
        </BoxWrapper>
        <Pagination
          currentPage={currentPage}
          onPageChange={(page) => {
            setCurrentPage(page);
          }}
          siblingCount={1}
          totalCount={data?.meta?.totalPages || 1}
          pageSize={10}
        />
      </section>
    </AdminRoute>
  );
}
